import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import useToDo from 'hooks/useToDo';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { gridSpacing } from 'store/constant';
import AnimateButton from 'views/components/extended/AnimateButton';
import Autocomplete from 'views/components/inputs/Autocomplete';
import DatePicker from 'views/components/inputs/DatePicker';
import FormContainer from 'views/components/inputs/FormContainer';
import TextField from 'views/components/inputs/TextField';
import * as Yup from 'yup';
const useStyles = makeStyles((theme: Theme) => ({
  reviewDialog: {
    '&>div:nth-child(3)': {
      '&>div': {
        maxWidth: '800px',
        width: '800px'
      }
    }
  },
  dialogPaper: {},
  reviewContainer: {
    marginTop: '0px',
    marginBottom: '0px'
  }
}));

export type ReviewEditProps = {
  todo?: any;
  handleCloseDialog: () => void;
  selectedDate: any;
};

const ToDoAdd: React.FC<ReviewEditProps> = ({ todo, handleCloseDialog, selectedDate }) => {
  const classes = useStyles();
  const { createToDo, isLoading, updateToDo } = useToDo(selectedDate);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(intl.formatMessage({ defaultMessage: 'Task is required' })),
    dueDate: Yup.date()
      .required(intl.formatMessage({ defaultMessage: 'Due Date is required' }))
      .min(today, intl.formatMessage({ defaultMessage: 'Due Date cannot be in the past' }))
      .transform((curr, orig) => (orig === '' ? null : curr))
      .typeError(intl.formatMessage({ defaultMessage: 'Due Date is not valid' })),
    priority: Yup.string()
      .nullable()
      .required(intl.formatMessage({ defaultMessage: 'Priority is required' }))
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const initialValues = useMemo(
    () => ({
      title: todo?.title || '',
      dueDate: todo?.dueDate || '',
      priority: todo?.priority || ''
    }),
    [todo]
  );

  const onFormSubmit = useCallback(
    async (payload: FormValues) => {
      try {
        if (todo) {
          await updateToDo({ id: todo?._id, payload });
          enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Todo updated successfully' }), { variant: 'success' });
        } else {
          await createToDo(payload);
          enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Todo created successfully' }), { variant: 'success' });
        }
      } catch (err: any) {
        console.error(err);
      }
      handleCloseDialog();
    },
    [handleCloseDialog, createToDo, enqueueSnackbar, intl, updateToDo, todo]
  );

  const priorityOptions = {
    High: intl.formatMessage({ defaultMessage: 'High' }),
    Medium: intl.formatMessage({ defaultMessage: 'Medium' }),
    Low: intl.formatMessage({ defaultMessage: 'Low' })
  };

  return (
    <Dialog open onClose={handleCloseDialog} classes={{ paper: classes.dialogPaper }} className={classes.reviewDialog}>
      <FormContainer validation={validationSchema} defaultValues={initialValues} onSuccess={onFormSubmit}>
        <DialogTitle>
          {todo ? <FormattedMessage defaultMessage="Edit To Do" /> : <FormattedMessage defaultMessage="Add To Do" />}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={gridSpacing} className={classes.reviewContainer}>
            <Grid item xs={12}>
              <TextField fullWidth name="title" label={intl.formatMessage({ defaultMessage: 'Task' })} required />
            </Grid>
            <Grid item xs={12}>
              <DatePicker name="dueDate" label={intl.formatMessage({ defaultMessage: 'Due Date' })} required minDate={today} />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <FormControl fullWidth required>
                <Autocomplete
                  options={Object.keys(priorityOptions)}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option) => priorityOptions[option]}
                  name="priority"
                  label={intl.formatMessage({ defaultMessage: 'Select Priority' }) + ' *'}
                  placeholder={intl.formatMessage({ defaultMessage: 'Select Priority' })}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button disabled={isLoading} type="submit" variant="contained">
              <FormattedMessage defaultMessage="Save" />
            </Button>
          </AnimateButton>
          <Button variant="text" onClick={handleCloseDialog}>
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};

export default ToDoAdd;
